import { useEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { HomePage } from './pages/HomePage.js';
import { TemplatesPage } from './pages/TemplatesPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { InsightsPage } from './pages/InsightsPage.js';
import { PresentationsPage } from './pages/PresentationsPage.js';
import { PresentationEditorPage } from './pages/PresentationEditorPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { DataSourcesAdminPage } from './pages/admin/DataSourcesAdminPage.js';
import { AuditAdminPage } from './pages/admin/AuditAdminPage.js';
import { DrillTargetsAdminPage } from './pages/admin/DrillTargetsAdminPage.js';
import { AuthoringStandardsPage } from './pages/admin/AuthoringStandardsPage.js';
import { ApiKeysAdminPage } from './pages/admin/ApiKeysAdminPage.js';
import { BrandAssetsPage } from './pages/admin/BrandAssetsPage.js';
import { ClaudeCodePage } from './pages/admin/ClaudeCodePage.js';
import { SchedulesAdminPage } from './pages/admin/SchedulesAdminPage.js';
import { CubesAdminPage } from './pages/admin/CubesAdminPage.js';
import { WhoAmIPage } from './pages/WhoAmIPage.js';
import { HelpPage } from './pages/help/HelpPage.js';
import { CutoverDashboard } from './pages/CutoverDashboard.js';
import { useAuthSession } from './auth/AuthBootstrap.js';
import { TenantSwitcher } from './components/TenantSwitcher.js';
import { parseDevToken } from './auth/devToken.js';
import { useAppChrome, type NavMode, type ThemeMode } from './shell/useAppChrome.js';
import { DrillthroughListener } from './shell/drill-through.js';
import { isEmbedMode } from './shell/embed.js';
import { useIsInternalAdmin } from './api/me.js';
import canopyLogoUrl from './assets/canopy-logo.svg';
import canopyMarkUrl from './assets/canopy-mark.svg';

/**
 * Top-level SPA shell.
 *
 * Layout:
 *   - Sidebar (left): Canopy mark + "Sage" accent, collapse toggle in
 *     the top-right of the brand row, primary authoring nav (Templates
 *     / Reports / Dashboards), and a bottom-of-rail Settings entry
 *     gated on any *Admin role.
 *   - Top bar (right): theme toggle + user-menu dropdown (Session, Sign out).
 *   - Collapsed sidebar shows only the Canopy mark (no wordmark, no
 *     "Sage" accent) — clean icon rail.
 *
 * "Insights" is the live-dashboard surface; the route still works under
 * /insights for legacy bookmarks but the canonical path is /dashboards.
 */
/**
 * Embed mode (?embed=1 — the Canopy portal embeds Sage as the Insights
 * module that way): the SPA hides its own sidebar + top bar, because the
 * portal owns the outer chrome and rendering both produces a double-frame.
 * Without the query param Sage renders its own chrome as before. See
 * shell/embed.ts for why the flag is latched across in-app navigation.
 */
export function App(): JSX.Element {
  const chrome = useAppChrome();
  const embed = isEmbedMode();
  return (
    <div className="app-shell" data-nav={embed ? 'embedded' : chrome.nav}>
      <DrillthroughListener />
      {!embed && <Sidebar chrome={chrome} />}
      <main className="content">
        {!embed && <TopBar chrome={chrome} />}
        {embed && <EmbedAdminBar />}
        <div className="content-body">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/:id" element={<TemplatesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/:id" element={<ReportsPage />} />
            {/* Dashboards is the canonical path; /insights stays as a
                legacy alias so old bookmarks keep working. */}
            <Route path="/dashboards" element={<InsightsPage />} />
            <Route path="/dashboards/:id" element={<InsightsPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:id" element={<InsightsPage />} />
            <Route path="/presentations" element={<PresentationsPage />} />
            <Route path="/presentations/new" element={<PresentationEditorPage />} />
            <Route path="/presentations/:id/edit" element={<PresentationEditorPage />} />
            <Route path="/presentations/:id" element={<PresentationsPage />} />
            <Route path="/cutover" element={<CutoverDashboard />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/data-sources" element={<DataSourcesAdminPage />} />
            <Route path="/admin/drill-targets" element={<DrillTargetsAdminPage />} />
            <Route path="/admin/standards" element={<AuthoringStandardsPage />} />
            <Route path="/admin/api-keys" element={<ApiKeysAdminPage />} />
            <Route path="/admin/brand-assets" element={<BrandAssetsPage />} />
            <Route path="/admin/claude-code" element={<ClaudeCodePage />} />
            <Route path="/admin/schedules" element={<SchedulesAdminPage />} />
            <Route path="/admin/cubes" element={<CubesAdminPage />} />
            <Route path="/admin/audit" element={<AuditAdminPage />} />
            <Route path="/whoami" element={<WhoAmIPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/help/getting-started" element={<HelpPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

interface SidebarProps {
  chrome: {
    theme: ThemeMode;
    nav: NavMode;
    toggleTheme(): void;
    toggleNav(): void;
  };
}

function Sidebar({ chrome }: SidebarProps): JSX.Element {
  const collapsed = chrome.nav === 'collapsed';
  const session = useAuthSession();
  const parsed = parseDevToken(session.token);
  // Any *Admin role unlocks Settings. Mirrors backend role gates on
  // /admin/* endpoints — we don't try to be more restrictive than them.
  const isAdmin = (parsed?.roles ?? []).some((r) => r === 'TenantAdmin' || r.endsWith('Admin'));
  return (
    <aside className="nav">
      {/* Brand row: mark on the left, "Sage" sits as a small accent
          up-right of the wordmark, collapse toggle at far right.
          When collapsed the row reduces to just the mark + toggle. */}
      <div className="nav-brand">
        {collapsed ? (
          <img className="brand-logo brand-logo--mark" src={canopyMarkUrl} alt="Canopy Sage" />
        ) : (
          <div className="brand-wordmark">
            <img className="brand-logo" src={canopyLogoUrl} alt="Canopy" />
            <span className="brand-product-accent">Sage</span>
          </div>
        )}
        <button
          type="button"
          className="nav-collapse-toggle"
          onClick={chrome.toggleNav}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronsRightIcon /> : <ChevronsLeftIcon />}
        </button>
      </div>

      <nav className="stack-sm" style={{ marginBottom: 'var(--space-3)' }}>
        <NavItem to="/" icon={<HomeIcon />} label="Home" collapsed={collapsed} end />
      </nav>
      <div className="nav-section-label">Authoring</div>
      <nav className="stack-sm">
        <NavItem to="/templates" icon={<TemplateIcon />} label="Templates" collapsed={collapsed} />
        <NavItem to="/reports" icon={<ReportIcon />} label="Reports" collapsed={collapsed} />
        <NavItem
          to="/dashboards"
          icon={<InsightsIcon />}
          label="Dashboards"
          collapsed={collapsed}
        />
        <NavItem
          to="/presentations"
          icon={<PresentationIcon />}
          label="Presentations"
          collapsed={collapsed}
        />
      </nav>

      {/* Bottom-of-rail admin entry. Admin-only so non-admin operators
          don't even see the surface — consistent with the other Canopy
          apps (each puts Settings at the bottom-left of its rail). */}
      {isAdmin && (
        <nav className="nav-bottom-section">
          <NavItem
            to="/cutover"
            icon={<CutoverIcon />}
            label="Cutover Parity"
            collapsed={collapsed}
          />
          <NavItem
            to="/admin"
            icon={<GearIcon size={16} />}
            label="Settings"
            collapsed={collapsed}
          />
        </nav>
      )}
    </aside>
  );
}

/**
 * Embedded admin navigation. In the portal there is no Sage sidebar, so the
 * admin console (/admin/*, /cutover) — opened from the Insights Admin section
 * — needs its own way around and back. Renders only on those admin routes, so
 * the Insights view is unchanged.
 *
 * "Back to Insights" is NOT gated on the session: an org switch in the portal
 * mid-visit re-fetches /me/tenants, and a customer-node scope is not internal.
 * Gating the whole bar stranded the admin on an admin page with no way out
 * but reloading the portal. Only the admin links need the internal-admin gate
 * (the same one as the entry that leads here).
 */
export function EmbedAdminBar(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const isInternalAdmin = useIsInternalAdmin();
  if (!isAdminPath(location.pathname)) return null;
  // Settings stands for the whole console except the pages with their own link.
  const settingsActive =
    location.pathname.startsWith('/admin') && !location.pathname.startsWith('/admin/cubes');
  return (
    <nav className="embed-admin-bar" aria-label="Sage admin">
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => navigate('/insights?embed=1')}
      >
        ← Back to Insights
      </button>
      {isInternalAdmin && (
        <>
          <NavLink to="/admin" className={() => linkClass({ isActive: settingsActive })}>
            Settings
          </NavLink>
          <NavLink to="/admin/cubes" className={linkClass}>
            Cubes
          </NavLink>
          <NavLink to="/cutover" className={linkClass}>
            Cutover parity
          </NavLink>
        </>
      )}
    </nav>
  );
}

function isAdminPath(path: string): boolean {
  return path.startsWith('/admin') || path.startsWith('/cutover');
}

function NavItem({
  to,
  icon,
  label,
  collapsed,
  badge,
  end,
}: {
  to: string;
  icon: JSX.Element;
  label: string;
  collapsed: boolean;
  badge?: string;
  end?: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      className={linkClass}
      {...(end ? { end: true } : {})}
      {...(collapsed ? { title: label } : {})}
    >
      {icon}
      <span className="nav-link-label">{label}</span>
      {badge && <span className="nav-badge">{badge}</span>}
    </NavLink>
  );
}

function CutoverIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5h5M4 10h5M4 15h5" />
      <path d="M14.5 4.5l2 2-2 2M11 13.5l2 2 4-4" />
    </svg>
  );
}

interface TopBarProps {
  chrome: {
    theme: ThemeMode;
    toggleTheme(): void;
  };
}

function TopBar({ chrome }: TopBarProps): JSX.Element {
  const location = useLocation();
  const session = useAuthSession();
  const parsed = parseDevToken(session.token);
  const crumb = pathCrumb(location.pathname);
  return (
    <header className="content-header">
      <div className="row row--baseline flex-1" style={{ gap: 'var(--space-2)' }}>
        <span
          className="text-mute text-xs"
          style={{ letterSpacing: 'var(--letter-wide)', textTransform: 'uppercase' }}
        >
          {crumb.section}
        </span>
        {crumb.detail && (
          <>
            <span className="text-mute">/</span>
            <span className="text-sm">{crumb.detail}</span>
          </>
        )}
      </div>
      <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
        {parsed && (
          <TenantSwitcher
            token={session.token}
            tenantId={parsed.tenantId}
            tenantShort={parsed.tenantShort}
          />
        )}
        <button
          type="button"
          className="topbar-iconbtn"
          onClick={chrome.toggleTheme}
          title={chrome.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle color theme"
        >
          {chrome.theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        <UserMenu userShort={parsed?.userShort ?? 'session'} />
      </div>
    </header>
  );
}

/**
 * UserMenu — top-bar avatar/initials button with a dropdown to the
 * Session page and a sign-out action. Sign-out clears the in-browser
 * dev token (the only source of session truth in dev) and reloads;
 * AuthBootstrap will then prompt for a new token.
 */
function UserMenu({ userShort }: { userShort: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  function handleSignOut(): void {
    try {
      localStorage.removeItem('canopy.dev.token');
    } catch {
      /* private mode */
    }
    window.location.reload();
  }

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="user-menu-avatar">{initials(userShort)}</span>
        <span className="user-menu-name">{userShort}</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="user-menu-pop" role="menu">
          <button
            type="button"
            role="menuitem"
            className="user-menu-item"
            onClick={() => {
              setOpen(false);
              navigate('/whoami');
            }}
          >
            <UserIcon />
            <span>Session details</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="user-menu-item user-menu-item--danger"
            onClick={handleSignOut}
          >
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

function initials(text: string): string {
  const cleaned = text.replace(/[^A-Za-z0-9]+/g, ' ').trim();
  if (!cleaned) return '?';
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/* AdminFab removed — admin entry now lives at the bottom-left of the
 * sidebar as a single Settings nav item, gated on any *Admin role.
 * The full admin overview at /admin lists every destination so users
 * get a one-click landing page instead of a floating popover. */

function pathCrumb(path: string): { section: string; detail: string | null } {
  if (path === '/') return { section: 'Canopy', detail: 'Home' };
  if (path.startsWith('/reports')) return { section: 'Reports', detail: null };
  if (path.startsWith('/dashboards') || path.startsWith('/insights'))
    return { section: 'Dashboards', detail: null };
  if (path.startsWith('/presentations')) return { section: 'Presentations', detail: null };
  if (path.startsWith('/templates')) return { section: 'Templates', detail: null };
  if (path.startsWith('/admin/data-sources')) return { section: 'Admin', detail: 'Data Sources' };
  if (path.startsWith('/admin/drill-targets')) return { section: 'Admin', detail: 'Drill-through' };
  if (path.startsWith('/admin/standards')) return { section: 'Admin', detail: 'Standards' };
  if (path.startsWith('/admin/api-keys')) return { section: 'Admin', detail: 'API Keys' };
  if (path.startsWith('/admin/brand-assets')) return { section: 'Admin', detail: 'Brand assets' };
  if (path.startsWith('/admin/claude-code')) return { section: 'Admin', detail: 'Claude Code' };
  if (path.startsWith('/admin/schedules')) return { section: 'Admin', detail: 'Schedules' };
  if (path.startsWith('/admin/audit')) return { section: 'Admin', detail: 'Audit' };
  if (path.startsWith('/admin')) return { section: 'Admin', detail: null };
  if (path.startsWith('/whoami')) return { section: 'Account', detail: 'Session' };
  if (path.startsWith('/help')) return { section: 'Account', detail: 'Getting started' };
  return { section: 'Canopy', detail: null };
}

function linkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav-link nav-link--active' : 'nav-link';
}

/* ─── Icons (inline SVG, one color, 16px) ───────────────────────── */

function icon(path: JSX.Element): JSX.Element {
  return (
    <svg
      className="nav-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}

function HomeIcon(): JSX.Element {
  return icon(
    <>
      <path d="M2 7l6-5 6 5v7a1 1 0 01-1 1H3a1 1 0 01-1-1V7z" />
      <path d="M6 14v-4h4v4" />
    </>,
  );
}

function TemplateIcon(): JSX.Element {
  return icon(
    <>
      <path d="M3 2h7l3 3v9a.8.8 0 0 1-.8.8H3.8A.8.8 0 0 1 3 14V2.8A.8.8 0 0 1 3 2z" />
      <path d="M10 2v3h3" />
      <path d="M5.5 8h5" />
      <path d="M5.5 11h3" />
    </>,
  );
}

function ReportIcon(): JSX.Element {
  return icon(
    <>
      <rect x="4" y="2" width="9" height="11" rx="1" />
      <path d="M6 5h5" />
      <path d="M6 7.5h5" />
      <path d="M6 10h3" />
      <path d="M2 4.5v9.5a.8.8 0 0 0 .8.8h8.7" />
    </>,
  );
}

function InsightsIcon(): JSX.Element {
  // Used for the Dashboards nav item — bar-chart silhouette.
  return icon(
    <>
      <path d="M2.5 12.5V3.5" />
      <path d="M13.5 12.5H2.5" />
      <path d="M5 11V8" />
      <path d="M8 11V5.5" />
      <path d="M11 11V7" />
    </>,
  );
}

function PresentationIcon(): JSX.Element {
  // Slide / projection screen silhouette for the Presentations nav item.
  return icon(
    <>
      <rect x="2" y="2.5" width="12" height="8" rx="1" />
      <path d="M8 10.5v2" />
      <path d="M5.5 13.5L8 12l2.5 1.5" />
    </>,
  );
}

function UserIcon(): JSX.Element {
  return icon(
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M3 14c0-2.5 2.24-4.5 5-4.5s5 2 5 4.5" />
    </>,
  );
}

function GearIcon({ size = 16 }: { size?: number } = {}): JSX.Element {
  return (
    <svg
      className="nav-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function LogoutIcon(): JSX.Element {
  return icon(
    <>
      <path d="M6 14H3.5A.5.5 0 0 1 3 13.5v-11A.5.5 0 0 1 3.5 2H6" />
      <path d="M11 11.5L13.5 8 11 4.5" />
      <path d="M13 8H6.5" />
    </>,
  );
}

function ChevronDownIcon(): JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6l5 5 5-5" />
    </svg>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.8 2.8l1.1 1.1M12.1 12.1l1.1 1.1M2.8 13.2l1.1-1.1M12.1 3.9l1.1-1.1" />
    </svg>
  );
}

function MoonIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M13.5 9.5A6 6 0 116.5 2.5a4.5 4.5 0 007 7z" />
    </svg>
  );
}

function ChevronsLeftIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 12L6 8l4-4" />
      <path d="M6 12L2 8l4-4" />
    </svg>
  );
}

function ChevronsRightIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 12l4-4-4-4" />
      <path d="M10 12l4-4-4-4" />
    </svg>
  );
}
